//! This is a Jotai atom that manages the state of the scheduled tasks menu.

import { editorInteractionStateFileUuidAtom } from '@/app/atoms/editorInteractionStateAtom';
import { closeRightSidePanels, registerRightSidePanelCloser } from '@/app/helpers/closeRightSidePanels';
import { scheduledTasksAPI } from '@/shared/api/scheduledTasksClient';
import { SEARCH_PARAMS } from '@/shared/constants/routes';
import { useFileRouteLoaderData } from '@/shared/hooks/useFileRouteLoaderData';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { atom, getDefaultStore, useAtom, useAtomValue } from 'jotai';
import { TeamPermissionSchema } from 'quadratic-shared/typesAndSchemas';
import type { ScheduledTask, ScheduledTaskLog } from 'quadratic-shared/typesAndSchemasScheduledTasks';
import type { SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const { TEAM_VIEW } = TeamPermissionSchema.enum;

export const CREATE_TASK_ID = 'CREATE';

export interface ScheduledTasks {
  show: boolean;
  currentTaskId: string | typeof CREATE_TASK_ID | null;
  tasks: ScheduledTask[];
}

const defaultScheduledTasks: ScheduledTasks = {
  show: false,
  currentTaskId: null,
  tasks: [],
};

const scheduledTasksBaseAtom = atom<ScheduledTasks>(defaultScheduledTasks);

export const scheduledTasksAtom = atom(
  (get) => get(scheduledTasksBaseAtom),
  (get, set, update: SetStateAction<ScheduledTasks>) => {
    const oldValue = get(scheduledTasksBaseAtom);
    const newValue = typeof update === 'function' ? update(oldValue) : update;

    // Right-side panel exclusivity: opening this panel closes the others.
    if (!oldValue.show && newValue.show) {
      closeRightSidePanels('scheduledTasks');
    }

    set(scheduledTasksBaseAtom, newValue);
  }
);

// Writes to the base atom to avoid re-entering the exclusivity logic in the
// setter above.
registerRightSidePanelCloser('scheduledTasks', () => {
  const store = getDefaultStore();
  const prev = store.get(scheduledTasksBaseAtom);
  if (prev.show) {
    store.set(scheduledTasksBaseAtom, { ...prev, show: false });
  }
});

// Direct function to show scheduled tasks without React hooks
export const showScheduledTasksDialog = (taskId?: string | typeof CREATE_TASK_ID) => {
  const store = getDefaultStore();
  const currentState = store.get(scheduledTasksAtom);
  store.set(scheduledTasksAtom, {
    ...currentState,
    show: true,
    currentTaskId: taskId ?? null,
  });
};

export const useLoadScheduledTasks = () => {
  const [, setScheduledTasks] = useAtom(scheduledTasksAtom);
  const fileUuid = useAtomValue(editorInteractionStateFileUuidAtom);
  const fileRouteData = useFileRouteLoaderData();
  const canViewScheduledTasks = Boolean(fileRouteData?.userMakingRequest.teamPermissions?.includes(TEAM_VIEW));
  const hasCheckedUrlParam = useRef(false);

  useEffect(() => {
    if (fileUuid && canViewScheduledTasks) {
      scheduledTasksAPI
        .get(fileUuid)
        .then((tasks) => {
          setScheduledTasks((prev) => ({ ...prev, tasks }));
        })
        .catch((error) => {
          // Silently handle error - tasks will remain empty
          // This can happen due to network issues or if the file doesn't exist
          console.warn('Failed to load scheduled tasks:', error);
        });

      // Check URL param regardless of API success (only once)
      if (!hasCheckedUrlParam.current) {
        hasCheckedUrlParam.current = true;
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has(SEARCH_PARAMS.SCHEDULED_TASKS.KEY)) {
          setScheduledTasks((prev) => ({ ...prev, show: true }));

          searchParams.delete(SEARCH_PARAMS.SCHEDULED_TASKS.KEY);
          const newUrl = searchParams.toString()
            ? `${window.location.pathname}?${searchParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  }, [canViewScheduledTasks, fileUuid, setScheduledTasks]);
};

export interface ScheduledTaskToSave {
  uuid: string;
  cronExpression: string;
  operations: Uint8Array<ArrayBuffer>;
}

interface ScheduledTasksActions {
  showScheduledTasks: (taskId?: string) => void;
  closeScheduledTasks: () => void;
  newScheduledTask: () => void;
  saveScheduledTask: (task: ScheduledTaskToSave) => Promise<void>;
  scheduledTasks: ScheduledTasks;
  currentTask: ScheduledTask | null;
  currentTaskId: string | null;
  deleteScheduledTask: (taskId: string) => void;
  show: boolean;
  getHistory: () => Promise<ScheduledTaskLog[]>;
}

export const useScheduledTasks = (): ScheduledTasksActions => {
  const [scheduledTasks, setScheduledTasks] = useAtom(scheduledTasksAtom);
  const fileUuid = useAtomValue(editorInteractionStateFileUuidAtom);

  const showScheduledTasks = useCallback(
    (taskId?: string) => {
      setScheduledTasks((prev) => {
        trackEvent('[ScheduledTasks].open', {
          taskId: taskId ?? null,
          hasExistingTasks: prev.tasks.length > 0,
        });
        return { ...prev, show: true, currentTaskId: taskId ?? null };
      });
    },
    [setScheduledTasks]
  );

  const closeScheduledTasks = useCallback(() => {
    setScheduledTasks((prev) => ({ ...prev, show: false }));
    trackEvent('[ScheduledTasks].close');
  }, [setScheduledTasks]);

  const newScheduledTask = useCallback(() => {
    setScheduledTasks((prev) => ({ ...prev, currentTaskId: CREATE_TASK_ID }));
  }, [setScheduledTasks]);

  const currentTask = useMemo(() => {
    if (scheduledTasks.currentTaskId) {
      return scheduledTasks.tasks.find((task) => task.uuid === scheduledTasks.currentTaskId) ?? null;
    }
    return null;
  }, [scheduledTasks.currentTaskId, scheduledTasks.tasks]);

  const saveScheduledTask = useCallback(
    async (task: ScheduledTaskToSave) => {
      if (!fileUuid) return;

      if (scheduledTasks.currentTaskId === CREATE_TASK_ID) {
        const created = await scheduledTasksAPI.create(fileUuid, {
          cronExpression: task.cronExpression,
          operations: Array.from(task.operations),
        });
        setScheduledTasks((prev) => ({
          ...prev,
          tasks: [created, ...prev.tasks],
        }));
      } else {
        const updated = await scheduledTasksAPI.update(fileUuid, task.uuid, {
          cronExpression: task.cronExpression,
          operations: Array.from(task.operations),
        });
        setScheduledTasks((prev) => ({
          ...prev,
          tasks: prev.tasks.map((task) => (task.uuid === updated.uuid ? updated : task)),
        }));
      }
    },
    [fileUuid, scheduledTasks.currentTaskId, setScheduledTasks]
  );

  const deleteScheduledTask = useCallback(
    async (taskId: string) => {
      if (!fileUuid) return;
      await scheduledTasksAPI.delete(fileUuid, taskId);
      setScheduledTasks((prev) => {
        const taskToDelete = prev.tasks.find((task) => task.uuid === taskId);
        trackEvent('[ScheduledTasks].delete', {
          taskUuid: taskId,
          cronExpression: taskToDelete?.cronExpression,
        });
        return { ...prev, tasks: prev.tasks.filter((task) => task.uuid !== taskId) };
      });
    },
    [fileUuid, setScheduledTasks]
  );

  const getHistory = useCallback(
    async (pageNumber = 1, pageSize = 10) => {
      if (!fileUuid || !currentTask?.uuid) return [];
      return scheduledTasksAPI.history(fileUuid, currentTask?.uuid ?? '', pageNumber, pageSize);
    },
    [fileUuid, currentTask?.uuid]
  );

  return {
    show: scheduledTasks.show,
    showScheduledTasks,
    closeScheduledTasks,
    newScheduledTask,
    scheduledTasks,
    currentTask,
    currentTaskId: scheduledTasks.currentTaskId,
    saveScheduledTask,
    deleteScheduledTask,
    getHistory,
  };
};
